import { useColorScheme } from "@/hooks/useColorScheme";

import { EnumColors } from "@/constants/values";

/**
 * Hook to get the appropriate color value based on the current color scheme.
 *
 * @param enumColor - An object containing color values for different color schemes
 * @returns The color value for the current color scheme
 */
export function useEnumColor(
  enumColor: (typeof EnumColors)[keyof typeof EnumColors],
  mode?: "light" | "dark" | "inverted",
) {
  const { colorScheme } = useColorScheme();

  const adjustedEnumColor = getEnumColor(enumColor, mode, colorScheme);

  return {
    ...adjustedEnumColor,
    solidColor: enumColor.solidColor,
  };
}

/**
 * Gets the appropriate color value from an enum color object based on the specified mode and color scheme.
 *
 * @param enumColor - An object containing color values for different color schemes
 * @param mode - Optional mode override ("light", "dark", or "inverted")
 * @param colorScheme - The current color scheme ("light", "dark", or "inverted")
 * @returns The color value for the determined color scheme
 */
export function getEnumColor(
  enumColor: (typeof EnumColors)[keyof typeof EnumColors],
  mode?: "light" | "dark" | "inverted",
  colorScheme?: "light" | "dark" | "inverted",
) {
  let adjustedColorScheme = colorScheme;

  if (mode === "inverted") {
    adjustedColorScheme = colorScheme === "light" ? "dark" : "light";
  } else if (mode != null) {
    adjustedColorScheme = mode;
  }

  return enumColor[adjustedColorScheme];
}
